/**
 * The Secure room's composer (client-chat-screen D7; client-chat-thread.html frames 1 to 3):
 * a text pill and the round send button, muted while empty, filled once there is text. The
 * behavior is the admin composer's, shared through `useChatComposer` (drafts, 1 to 4096
 * characters, the tap send and its reduced-motion fallback); this is the warm skin.
 * No attach button: the Secure room carries text.
 */
import { motion } from 'framer-motion';
import { SEND_GLYPH_PATH, useChatComposer } from '@/components/chat/useChatComposer';
import type { SendOrigin } from '@/components/chat/sendLanding';
import styles from './ClientChatScreen.module.css';

export interface SecureComposerProps {
  draftScope: string;
  placeholder: string;
  /** The keyboard is up: the home-indicator padding gives way (frame 2). */
  raised: boolean;
  onSend: (text: string, origin: SendOrigin | null) => void;
  onFocus?: () => void;
  /** A keystroke with words in the pill (the typing signal, D11). */
  onType?: () => void;
}

export function SecureComposer({ draftScope, placeholder, raised, onSend, onFocus, onType }: SecureComposerProps) {
  const composer = useChatComposer({ draftScope, onSend, onType });
  const { text, change, inputRef, pillRef, live, flying, tooLong } = composer;
  return (
    <div className={`${styles.composer} ${raised ? styles.raised : ''}`} data-testid="secure-composer">
      {tooLong && <p className={styles.limit} role="status">Keep it under 4096 characters.</p>}
      <div className={styles.pill} ref={pillRef} onClick={composer.focusPill}>
        <textarea
          ref={inputRef}
          className={styles.input}
          rows={1}
          value={text}
          onChange={change}
          onFocus={onFocus}
          placeholder={placeholder}
          aria-label="Message"
          enterKeyHint="enter"
        />
      </div>
      <motion.button
        type="button"
        className={`${styles.send} ${live || flying ? '' : styles.sendIdle}`}
        aria-label="Send"
        {...composer.sendButton}
      >
        <motion.svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={composer.glyphStyle}>
          <path transform="rotate(-45 12 12)" d={SEND_GLYPH_PATH} />
        </motion.svg>
      </motion.button>
    </div>
  );
}
